import {
    BulkUploadSupportedField,
    BulkUploadSupportedValuesTip,
} from '../components/bulk-upload-supported-values/bulk-upload-supported-values.model';

export type VehicleMonitoringTypeOption = {
    value: string;
    label: string;
};

/** Keep in sync with backend `VEHICLE_MONITORING_TYPE_CHOICES`. */
export const VEHICLE_MONITORING_TYPES: VehicleMonitoringTypeOption[] = [
    {value: 'GPS_INACTIVE', label: 'GPS Inactive'},
    {value: 'SERVICE_DUE', label: 'Service Due'},
    {value: 'HIGHER_DUES', label: 'Higher Dues'},
    {value: 'INSURANCE_DUE', label: 'Insurance Due'},
    {value: 'RC_EXPIRY', label: 'RC Expiry'},
    {value: 'PERMIT_EXPIRY', label: 'Permit Expiry'},
    {value: 'OTHERS', label: 'Others'},
];

export const VEHICLE_MONITORING_TYPE_LABELS = VEHICLE_MONITORING_TYPES.map(option => option.label);

/** Canonical badge display order — extend at end when adding monitoring types. */
export const MONITORING_TYPE_DISPLAY_ORDER = VEHICLE_MONITORING_TYPES.map(option => option.value);

const MONITORING_TYPE_SORT_INDEX = Object.fromEntries(
    MONITORING_TYPE_DISPLAY_ORDER.map((code, index) => [code, index]),
) as Record<string, number>;

export function sortMonitoringRulesByBusinessOrder<T extends {type: string}>(rules: T[]): T[] {
    return [...rules].sort(
        (left, right) =>
            (MONITORING_TYPE_SORT_INDEX[left.type] ?? MONITORING_TYPE_DISPLAY_ORDER.length)
            - (MONITORING_TYPE_SORT_INDEX[right.type] ?? MONITORING_TYPE_DISPLAY_ORDER.length),
    );
}

export const VEHICLE_MONITORING_TYPE_FILTER_OPTIONS: VehicleMonitoringTypeOption[] = [
    {value: '', label: 'All Monitoring Types'},
    ...VEHICLE_MONITORING_TYPES,
];

/** Keep in sync with backend `VEHICLE_ALERT_SEVERITY_CHOICES`. */
export const VEHICLE_MONITORING_SEVERITY_OPTIONS: VehicleMonitoringTypeOption[] = [
    {value: 'LOW', label: 'Low'},
    {value: 'MEDIUM', label: 'Medium'},
    {value: 'HIGH', label: 'High'},
    {value: 'CRITICAL', label: 'Critical'},
];

export const VEHICLE_MONITORING_DEFAULT_SEVERITY = 'LOW';

export type SeverityBadgeVariant = 'low' | 'medium' | 'high' | 'critical';

/** Badge colors — keep in sync with backend `SEVERITY_BADGE_STYLES` in vehicle_alert_rules.py */
const SEVERITY_BADGE_VARIANT_BY_CODE: Record<string, SeverityBadgeVariant> = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical',
};

const SEVERITY_LABEL_BY_CODE = Object.fromEntries(
    VEHICLE_MONITORING_SEVERITY_OPTIONS.map(option => [option.value, option.label]),
) as Record<string, string>;

export function resolveSeverityBadge(
    severity: string | null | undefined,
    displayLabel?: string | null | undefined,
): {label: string; variant: SeverityBadgeVariant} | null {
    const normalized = (severity ?? displayLabel ?? '').trim();
    if (!normalized) {
        return {
            label: SEVERITY_LABEL_BY_CODE[VEHICLE_MONITORING_DEFAULT_SEVERITY],
            variant: SEVERITY_BADGE_VARIANT_BY_CODE[VEHICLE_MONITORING_DEFAULT_SEVERITY],
        };
    }

    const upper = normalized.toUpperCase();
    if (SEVERITY_LABEL_BY_CODE[upper]) {
        return {
            label: SEVERITY_LABEL_BY_CODE[upper],
            variant: SEVERITY_BADGE_VARIANT_BY_CODE[upper] ?? 'low',
        };
    }

    const byLabel = VEHICLE_MONITORING_SEVERITY_OPTIONS.find(
        option => option.label.toLowerCase() === normalized.toLowerCase(),
    );
    if (byLabel) {
        return {
            label: byLabel.label,
            variant: SEVERITY_BADGE_VARIANT_BY_CODE[byLabel.value] ?? 'low',
        };
    }

    return {
        label: normalized,
        variant: 'low',
    };
}

export type MonitoringTypeBadgeVariant =
    | 'gps-inactive'
    | 'service-due'
    | 'higher-dues'
    | 'insurance-due'
    | 'rc-expiry'
    | 'permit-expiry'
    | 'others';

/** Badge colors — keep in sync with backend `MONITORING_TYPE_BADGE_STYLES` in vehicle_alert_rules.py */
const MONITORING_TYPE_BADGE_VARIANT_BY_CODE: Record<string, MonitoringTypeBadgeVariant> = {
    GPS_INACTIVE: 'gps-inactive',
    SERVICE_DUE: 'service-due',
    HIGHER_DUES: 'higher-dues',
    INSURANCE_DUE: 'insurance-due',
    RC_EXPIRY: 'rc-expiry',
    PERMIT_EXPIRY: 'permit-expiry',
    OTHERS: 'others',
};

const MONITORING_TYPE_LABEL_BY_CODE = Object.fromEntries(
    VEHICLE_MONITORING_TYPES.map(option => [option.value, option.label]),
) as Record<string, string>;

export function normalizeMonitoringTypeCode(value: string | null | undefined): string | null {
    const normalized = (value ?? '').trim();
    if (!normalized) {
        return null;
    }

    const upper = normalized.toUpperCase();
    if (MONITORING_TYPE_LABEL_BY_CODE[upper]) {
        return upper;
    }

    const byLabel = VEHICLE_MONITORING_TYPES.find(
        option => option.label.toLowerCase() === normalized.toLowerCase(),
    );
    return byLabel?.value ?? null;
}

export function resolveMonitoringTypeBadge(
    monitoringType: string | null | undefined,
    displayLabel?: string | null | undefined,
): {label: string; variant: MonitoringTypeBadgeVariant} | null {
    const code = normalizeMonitoringTypeCode(monitoringType) ?? normalizeMonitoringTypeCode(displayLabel);
    if (code) {
        return {
            label: MONITORING_TYPE_LABEL_BY_CODE[code] ?? code,
            variant: MONITORING_TYPE_BADGE_VARIANT_BY_CODE[code] ?? 'others',
        };
    }

    const fallbackLabel = (displayLabel ?? monitoringType ?? '').trim();
    if (!fallbackLabel) {
        return null;
    }

    return {
        label: fallbackLabel,
        variant: 'others',
    };
}

export const VEHICLE_MONITORING_BULK_UPLOAD_SUPPORTED_FIELDS: BulkUploadSupportedField[] = [
    {label: 'Monitoring Type', values: VEHICLE_MONITORING_TYPE_LABELS},
];

export const VEHICLE_MONITORING_BULK_UPLOAD_SUPPORTED_TIP: BulkUploadSupportedValuesTip = {
    lead: 'The ',
    emphasis: 'Monitoring Type',
    trail: ' must exactly match one of the values listed above.',
};
